import React, { useState, useEffect, useCallback } from 'react';
import ContentWrapper from '../Layout/ContentWrapper';
import * as jobService from './Services/jobService';
import JobForm from './Components/Forms/jobForm';
import * as utils from './Services/utils';
import swal from 'sweetalert';
import { useTranslation } from 'react-i18next';
import JobStatusButtons from './Components/shared/JobButtons';
import { useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';
import EditJobCompetenceModal from './Components/Modals/editJobCompetenceModal';
import JobCompetencesAndQuestionsCard from './Components/Cards/JobCompetencesAndQuestionsCard';
import QuestionModal from './Components/Modals/questionModal';
import { Col } from 'reactstrap';
import produce from 'immer';
import JobQuestionnaireCard from './Components/Cards/JobQuestionnaireCard';
import { QuestionType } from './Helpers/enums';

const JobDetails = () => {
  const [modal, setModal] = useState(false);
  // const [nestedModal, setNestedModal] = useState(false);
  const [competenceIndex, setCompetenceIndex] = useState(0);
  const [oneAtATime, setOneAtATime] = useState(false);
  // const [editCompetence, setEditCompetence] = useState(false);
  const [modalPurpose, setModalPurpose] = useState('');
  const [formCompetences, setFormCompetences] = useState({
    competenceId: '',
    competenceName: '',
    description: '',
    type: '',
    weight: '',
  });
  const [job, setJob] = useState({});
  const [questionId, setQuestionId] = useState(undefined);
  const [accordionState, setAccordionState] = useState([true, true, true]);
  const [questionModalVisible, setQuestionModalVisible] = useState(false);
  const [jobModalPurpose, setJobModalPurpose] = useState('');
  const [isCompetenceLoading, setIsCompetenceLoading] = useState(false);
  const [questionType, setQuestionType] = useState(QuestionType.Normal);

  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { id } = useParams();

  useEffect(() => {
    fetchJob();
  }, []);

  const fetchJob = async () => {
    if (id !== '0') {
      const { data: fetchedJob } = await jobService.getJob(id);
      setJob(fetchedJob);
    } else {
      const parsed = queryString.parse(location.search);
      if (parsed.language) {
        i18n.changeLanguage(parsed.language === 'sl-SI' || parsed.language === 'si' ? 'si' : 'en');
      }
    }
  };

  const toggleAccordion = useCallback(
    (index) => {
      setAccordionState((prevState) =>
        prevState.map((state, i) => (index === i ? !state : oneAtATime ? false : state))
      );
    },
    [oneAtATime]
  );

  const saveJobCompetence = async (values) => {
    setIsCompetenceLoading(true);
    try {
      if (modalPurpose === 'create') {
        const response = await jobService.createJobCompetence({
          jobId: job.id,
          competenceId: values.asyncSelectCompetence?.value,
          weight: values.competenceWeight?.value,
          description: values.competenceDescription,
          type: values.radioBtnsCompetenceType,
        });
        setJob(response.data);
        toggleModal('');
      } else if (modalPurpose === 'edit') {
        const updatedCompetences = [...job.jobCompetences];
        updatedCompetences[competenceIndex] = {
          ...updatedCompetences[competenceIndex],
          competenceName: values.asyncSelectCompetence?.label || values.competenceNameInput,
          type: values.radioBtnsCompetenceType,
          weight: values.competenceWeight?.value,
          description: values.competenceDescription,
        };

        await jobService.updateJobCompetence(updatedCompetences[competenceIndex], job.id);
        setJob({ ...job, jobCompetences: updatedCompetences });
        toggleModal('');
      }
    } finally {
      setIsCompetenceLoading(false);
    }
  };

  const saveJobQuestion = async (values) => {
    if (jobModalPurpose === 'create') {
      const { data: updatedJob } = await jobService.createJobQuestion({
        jobId: job.id,
        content: values.question,
        questionType: questionType,
      });
      setJob(updatedJob);
    } else {
      const { data: updatedJob } = await jobService.updateJobQuestion(
        job.id,
        values.questionId,
        values.question,
        questionType
      );
      setJob(updatedJob);
    }
    toggleQuestionModal();
  };

  const toggleModal = (purpose = '') => {
    setModal((prev) => !prev);
    setModalPurpose(purpose);
  };

  const toggleQuestionModal = (purpose = '') => {
    setQuestionModalVisible((prev) => !prev);
    setJobModalPurpose(purpose);
  };

  const toggleCompetenceModalForEdit = (competence, index) => {
    setModalPurpose('edit');
    //setEditCompetence(true);
    setFormCompetences(competence);
    setCompetenceIndex(index);
    setQuestionId(undefined);
    toggleModal('edit');
  };

  const deleteJobCompetence = (competence) => {
    swal(utils.getWarningSwal()).then((willDelete) => {
      if (willDelete) {
        jobService.deleteJobCompetence(job.id, competence.competenceId).then((response) => {
          setJob(response.data);
        });
      }
    });
  };

  const toggleJobQuestionModalForCreate = (qType) => {
    setQuestionType(qType);
    setJobModalPurpose('create');
    setQuestionModalVisible(true);
  };

  const toggleJobQuestionModalForEdit = (question, index) => {
    setJobModalPurpose('edit');
    setQuestionId(question.id);
    setCompetenceIndex(index);
    setQuestionModalVisible(true);
  };

  const deleteJobQuestion = (question) => {
    swal(utils.getWarningSwal()).then((willDelete) => {
      if (willDelete) {
        jobService.deleteJobQuestion(job.id, question.id).then((response) => {
          setJob(response.data);
        });
      }
    });
  };

  const createJobCompetence = () => {
    setModalPurpose('create');
    //setEditCompetence(true);
    setFormCompetences({
      competenceId: '',
      competenceName: '',
      description: '',
      type: '',
      weight: '',
    });
    toggleModal('create');
  };

  const refreshJob = (updatedJob) => {
    setJob(updatedJob);
  };

  const handleJobDescriptionChange = (val) => {
    setJob((prevJob) =>
      produce(prevJob, (draft) => {
        draft.description = val;
      })
    );
  };

  const getAccordionIcon = (position) =>
    accordionState[position] ? 'fa fa-minus text-primary mr-2' : 'fa fa-plus text-primary mr-2';

  const onDragQuestionEnd = (result) => {
    if (!result.destination) return;

    setIsCompetenceLoading(true);

    // Create a new array with reordered questions optimistically
    const reorderedQuestions = Array.from(job.questions);
    const [removed] = reorderedQuestions.splice(result.source.index, 1);
    reorderedQuestions.splice(result.destination.index, 0, removed);

    // Optimistically update the local state
    setJob((prevJob) => ({
      ...prevJob,
      questions: reorderedQuestions,
    }));

    // Make the API call to reorder questions
    jobService
      .reorderJobQuestions({ jobId: job.id, reorderedQuestions })
      .then((response) => {
        // Update the job with the new data from the response
        setJob(response.data);
      })
      .catch((error) => {
        //console.log({ error });
        // Optionally, roll back to the previous state if the API call fails
        setJob((prevJob) => ({
          ...prevJob,
          questions: job.questions, // Roll back to the previous state
        }));
      })
      .finally(() => {
        setIsCompetenceLoading(false);
      });
  };

  return (
    <ContentWrapper>
      {/* HEADER RIBBON */}
      {id === '0' ? (
        <div className="content-heading">
          <Col>
            {t('jobs.CREATE_NEW')}
            <small>{t('dashboard.WELCOME_BETA')}</small>
          </Col>
        </div>
      ) : (
        <div className="content-heading">
          <Col md={11}>
            {t('jobs.JOB')}: <b>{job.title}</b> ({job.jobStatus})<small>{t('dashboard.WELCOME_BETA')}</small>
          </Col>
          <Col>
            <JobStatusButtons job={job} btnSize="lg" onStatusRefresh={fetchJob} language={i18n.language} />
          </Col>
        </div>
      )}
      {/* END HEADER RIBBON */}

      <EditJobCompetenceModal
        isOpen={modal}
        modalPurpose={modalPurpose}
        formCompetences={formCompetences}
        competence={{}}
        toggle={toggleModal}
        saveJobCompetence={saveJobCompetence}
        saveJobQuestion={saveJobQuestion}
        questionId={questionId}
        isLoading={isCompetenceLoading}
      />
      <QuestionModal
        showQuestionModal={questionModalVisible}
        onToggle={toggleQuestionModal}
        saveJobQuestion={saveJobQuestion}
        question={job.questions?.find((q) => q.id === questionId)}
        modalPurpose={jobModalPurpose}
      />

      {/* Card JOB Description */}
      <JobForm
        isOpen={accordionState[0]}
        job={job}
        toggleAccordion={() => toggleAccordion(0)}
        getAccordionIcon={() => getAccordionIcon(0)}
        refreshJob={refreshJob}
        onDescriptionChange={handleJobDescriptionChange}
      />

      {/* Card Competences */}
      {id !== '0' && (
        <>
          <JobCompetencesAndQuestionsCard
            isOpen={accordionState[1]}
            toggleAccordion={() => toggleAccordion(1)}
            jobCompetences={job.jobCompetences}
            jobQuestions={job.questions?.filter((q) => q.questionType === QuestionType.Normal)}
            maxPossibleScore={job.maxPossibleScore}
            createJobCompetence={createJobCompetence}
            editJobCompetence={toggleCompetenceModalForEdit}
            deleteJobCompetence={deleteJobCompetence}
            createJobQuestion={() => toggleJobQuestionModalForCreate(QuestionType.Normal)}
            editJobQuestion={toggleJobQuestionModalForEdit}
            deleteJobQuestion={deleteJobQuestion}
            onDragQuestionEnd={onDragQuestionEnd}
          />
          <JobQuestionnaireCard
            isOpen={accordionState[2]}
            toggleAccordion={() => toggleAccordion(2)}
            createJobQuestion={() => toggleJobQuestionModalForCreate(QuestionType.ForQuestionnaire)}
            jobQuestionnaire={job.questions?.filter((q) => q.questionType === QuestionType.ForQuestionnaire)}
            deleteJobQuestion={deleteJobQuestion}
            editJobQuestion={toggleJobQuestionModalForEdit}
            onDragQuestionEnd={onDragQuestionEnd}
          />
        </>
      )}
    </ContentWrapper>
  );
};

export default JobDetails;
